import React, { useEffect, useState } from 'react'
import type { ResultOf } from 'gql.tada'
import { Button, Modal, Select, Typography } from 'antd'
import type { ButtonProps } from 'antd'

import {
  selectTestProps,
  withOptionTestIds,
} from '@components/common/testIds'
import { usePermissions } from '@hooks/auth/usePermissions'
import { GQL_CLIENT } from 'src/graphqlClient'
import { AccountType } from 'src/utils/enums'

import { SalesmanAssignmentCandidatesDocument } from '../services/salesmens.graphql'
import {
  useChangeSalesman,
  type ChangeSalesmanSubject,
} from '../hooks/useChangeSalesman'

const { Text } = Typography

/**
 * The roles offered this control. A commercial is absent on purpose: handing a
 * dossier on is the office's call or the company's, never the salesman's own.
 *
 * It mirrors the `change_salesman` action's `permissions` block, and it is a
 * DISPLAY rule only — the server re-asks the same question, so rendering the
 * button is never what authorises the change.
 */
const REASSIGNING_ROLES: readonly string[] = [
  AccountType.ARCHITECT,
  AccountType.COLLABORATOR,
  AccountType.SOCIETY,
]

export const mayChangeSalesman = (accountType: string | undefined): boolean =>
  accountType !== undefined && REASSIGNING_ROLES.includes(accountType)

type CandidatesData = ResultOf<typeof SalesmanAssignmentCandidatesDocument>

type SalesmanCandidate = CandidatesData['salesmens'][number]

type SalesmanOption = { value: number; label: string }

/**
 * The company qualifies the name for the office, which sees every roster and
 * can hold two commerciaux of the same first name. A company sees only its
 * own, so the suffix says nothing there — and is simply absent.
 */
const toOption = (salesman: SalesmanCandidate): SalesmanOption => {
  const name = [salesman.first_name, salesman.last_name].filter(Boolean).join(' ')
  const company = salesman.company?.name
  return {
    value: salesman.id,
    label: company ? `${name} — ${company}` : name,
  }
}

interface ChangeSalesmanControlProps extends ChangeSalesmanSubject {
  /** The commercial the resource carries today, pre-selected in the picker. */
  currentSalesmanId?: number | null | undefined
  /** Re-read the resource once it has changed hands. */
  onChanged?: () => void
  block?: boolean
  size?: ButtonProps['size']
  type?: ButtonProps['type']
  /** Overrides the default "Changer le commercial" wording. */
  label?: string
}

/**
 * "Changer le commercial" — reattach a dossier or a study to someone else
 * (#769).
 *
 * One component for four surfaces: the architect's project synthesis and study
 * editor, the company's project page and study page. Like
 * `CancelUrbanAnalysisButton`, no caller passes a "may I" flag — the answer is
 * the signed-in role, read here, and the server re-asks it.
 *
 * ABSENT rather than disabled for the roles that may not: a disabled control
 * offers something that will never become available to that account.
 */
export const ChangeSalesmanControl: React.FC<ChangeSalesmanControlProps> = ({
  projectId,
  urbanAnalysisId,
  currentSalesmanId,
  onChanged,
  block = false,
  size,
  type = 'default',
  label = 'Changer le commercial',
}) => {
  const { data: accountType } = usePermissions<AccountType>()
  const [open, setOpen] = useState(false)
  const [picked, setPicked] = useState<number | undefined>(undefined)
  const [options, setOptions] = useState<SalesmanOption[]>([])
  const [loading, setLoading] = useState(false)

  const { isSaving, change } = useChangeSalesman(
    { projectId, urbanAnalysisId },
    onChanged,
  )

  // Read only once the modal is open, and through `GQL_CLIENT` rather than
  // urql's `useQuery`: this control is mounted on four pages whose suites
  // provide no urql context, and a hook that demands one would turn each of
  // them into a provider-wiring exercise. `GQL_CLIENT` is that same client.
  useEffect(() => {
    if (!open) return

    let live = true
    setLoading(true)
    GQL_CLIENT.request(SalesmanAssignmentCandidatesDocument)
      .then((data: CandidatesData) => {
        if (!live) return
        setOptions((data?.salesmens ?? []).map(toOption))
      })
      .catch(() => {
        // An empty picker says "nobody to hand this to", which is the honest
        // read of a roster that could not be loaded — and the confirm button
        // stays disabled until something is picked.
        if (live) setOptions([])
      })
      .finally(() => {
        if (live) setLoading(false)
      })

    return () => {
      live = false
    }
  }, [open])

  const subjectNamed = projectId !== undefined ? 'ce dossier' : 'cette étude'
  const unchanged = picked === undefined || picked === currentSalesmanId

  const openModal = (): void => {
    setPicked(currentSalesmanId ?? undefined)
    setOpen(true)
  }

  const confirm = async (): Promise<void> => {
    if (picked === undefined) return
    const done = await change(picked)
    if (done) setOpen(false)
  }

  if (!mayChangeSalesman(accountType)) return null
  if (projectId === undefined && urbanAnalysisId === undefined) return null

  return (
    <>
      <Button
        block={block}
        size={size}
        type={type}
        onClick={openModal}
        data-cy="action--change-salesman"
      >
        {label}
      </Button>
      <Modal
        open={open}
        title="Changer le commercial"
        okText="Confirmer"
        cancelText="Annuler"
        onCancel={() => setOpen(false)}
        onOk={confirm}
        okButtonProps={{
          // Confirming the commercial already in place would be a write that
          // says nothing; the picker starts on them, so this is also what the
          // modal opens with.
          disabled: unchanged,
          loading: isSaving,
          'data-cy': 'action--change-salesman-confirm',
        }}
        cancelButtonProps={{ 'data-cy': 'action--change-salesman-dismiss' }}
        destroyOnClose
      >
        <Text type="secondary">
          À quel commercial rattacher {subjectNamed} ?
        </Text>
        <div style={{ marginTop: 12 }}>
          <Select
            {...selectTestProps('change-salesman-select')}
            style={{ width: '100%' }}
            showSearch
            optionFilterProp="label"
            loading={loading}
            value={picked}
            onChange={setPicked}
            options={withOptionTestIds(options)}
            placeholder="Sélectionnez un commercial"
          />
        </div>
        {projectId !== undefined && (
          <Text
            type="secondary"
            style={{ display: 'block', marginTop: 12, fontSize: 12 }}
          >
            Les études urbaines rattachées à ce dossier suivront le nouveau
            commercial.
          </Text>
        )}
      </Modal>
    </>
  )
}
